import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { searchAssessmentList } from '../../../../../utils/api_service/assessment_api';
import {
  Assessment,
  AssessmentsSearchObject,
} from '../../../../../utils/types/assessment_types';

export const useAssessments = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const tabs = [
    { label: 'Active Assessments' },
    { label: 'In-Active Assessments' },
  ];
  const [searchObject, setSearchObject] = useState<AssessmentsSearchObject>({
    timeRange: {
      startDate: dayjs().subtract(2, 'year').format('YYYY-MM-DD'), // Expand to 2 years for debugging
      endDate: dayjs().add(1, 'year').format('YYYY-MM-DD'), // Extend to future for debugging
    },
    assessmentName: '',
    page: 1,
    limit: 10,
    active: true,
    sortBy: 'creationDateTime',
    sortOrder: 'desc',
  });
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  // Update searchObject when activeTab changes
  useEffect(() => {
    setSearchObject((prevSearchObject) => ({
      ...prevSearchObject,
      timeRange: {
        startDate: dayjs().subtract(2, 'year').format('YYYY-MM-DD'), // Expand for debugging
        endDate: dayjs().add(1, 'year').format('YYYY-MM-DD'), // Extend to future for debugging
      },
      assessmentName: '',
      page: 1,
      active: activeTab === 0 ? true : false,
      sortBy: 'creationDateTime',
      sortOrder: 'desc',
    }));
    // Reset sort state to default
    setCurrentSort({ key: 'creationDateTime', direction: 'desc' });
  }, [activeTab]);

  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginatedAssessments, setPaginatedAssessments] =
    useState<Assessment[] | undefined>();
  const [totalRecords, setTotalRecords] = useState(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1); // Track the current page number
  const [currentSort, setCurrentSort] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'creationDateTime', direction: 'desc' });

  // Update searchObject and fetch data whenever the searchObject changes
  useEffect(() => {
    setLoading(true);

    // Ensure timeRange is always properly set
    const searchObjectWithValidTimeRange = {
      ...searchObject,
      timeRange: searchObject.timeRange || {
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
    };

    const debounceSearchTerm = setTimeout(() => {
      searchAssessmentList(searchObjectWithValidTimeRange)
        .then(async (response) => {
          // Debug: Check if the specific assessment is in the response
          const targetAssessment = response.assessments?.find(
            (a: any) => a.id === '68e05f28d78a17c3b010310e',
          );

          let assessments = response.assessments || [];

          // Client-side search fallback (simple title search for now)
          if (
            searchObject.assessmentName &&
            searchObject.assessmentName.trim() !== ''
          ) {
            const searchTerm = searchObject.assessmentName.toLowerCase().trim();
            assessments = assessments.filter((assessment: any) => {
              const titleMatch = assessment.assessmentTitle
                ?.toLowerCase()
                .includes(searchTerm);
              return titleMatch;
            });
          }

          // Temporary client-side sorting as fallback (for debugging)
          if (
            searchObjectWithValidTimeRange.sortBy &&
            searchObjectWithValidTimeRange.sortOrder &&
            assessments.length > 0
          ) {
            assessments = [...assessments].sort((a: any, b: any) => {
              let aValue, bValue;

              // Special handling for user field (combine firstName + lastName)
              if (currentSort?.key === 'user') {
                aValue = `${a.user?.firstName || ''} ${
                  a.user?.lastName || ''
                }`.trim();
                bValue = `${b.user?.firstName || ''} ${
                  b.user?.lastName || ''
                }`.trim();
              } else {
                aValue = a[searchObjectWithValidTimeRange.sortBy!];
                bValue = b[searchObjectWithValidTimeRange.sortBy!];
              }

              // Handle different data types
              let comparison = 0;
              if (typeof aValue === 'string' && typeof bValue === 'string') {
                comparison = aValue.localeCompare(bValue);
              } else if (
                typeof aValue === 'number' &&
                typeof bValue === 'number'
              ) {
                comparison = aValue - bValue;
              } else {
                // Convert to string for comparison
                comparison = String(aValue || '').localeCompare(
                  String(bValue || ''),
                );
              }

              const result =
                searchObjectWithValidTimeRange.sortOrder === 'desc'
                  ? -comparison
                  : comparison;

              return result;
            });
          }

          setTotalPages(
            Math.ceil(response.totalAssessments / searchObject.limit),
          );
          setTotalRecords(response.totalAssessments);
          setPaginatedAssessments(assessments);
          setHasFetchedOnce(true); // Add this
        })
        .catch((error) => {
          setPaginatedAssessments([]);
          setHasFetchedOnce(true); // Also here to avoid false "no data"
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchObject]); // This listens to all changes in searchObject

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const searchTerm = event.target.value;

    // Check if search term looks like an ID (numeric)
    const isNumericSearch = /^\d+$/.test(searchTerm.trim());

    setSearchObject((prev) => ({
      ...prev,
      timeRange: prev.timeRange || {
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      assessmentName: searchTerm,
      page: 1, // Reset to page 1 when searching
    }));
  };

  const history = useHistory();
  const onRowClick = (assessment: Assessment) => {
    history.push(`/view-assessment/${assessment.id}`);
  };

  // Handle page change and update searchObject
  const onPageChange = (page: number) => {
    setPageNumber(page); // Update the pageNumber for display
    setSearchObject((prev) => ({
      ...prev,
      timeRange: prev.timeRange || {
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      page, // Update the page in searchObject to fetch data for the new page
    }));
  };

  // Handle sorting - only one column at a time
  const handleSortChange = (key: string) => {
    const newDirection =
      currentSort?.key === key && currentSort?.direction === 'asc'
        ? 'desc'
        : 'asc';

    setCurrentSort({ key, direction: newDirection });

    // Map frontend column keys to backend field names
    let backendSortKey = key;
    if (key === 'user') {
      // For user sorting, we'll handle it differently in client-side fallback
      // Backend might need 'firstName' or a combined field
      backendSortKey = 'firstName'; // or 'user.firstName' depending on backend
    }

    // Update search object with sort parameters
    setSearchObject((prev) => {
      const newSearchObject: AssessmentsSearchObject = {
        ...prev,
        timeRange: prev.timeRange || {
          startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
          endDate: dayjs().format('YYYY-MM-DD'),
        },
        sortBy: backendSortKey,
        sortOrder: newDirection as 'asc' | 'desc',
        page: 1, // Reset to first page when sorting
      };
      return newSearchObject;
    });

    setPageNumber(1); // Reset page number display
  };

  return {
    activeTab,
    setActiveTab,
    searchObject,
    setSearchObject,
    tabs,
    loading,
    paginatedAssessments,
    totalPages,
    handleSearchTermChange,
    onRowClick,
    onPageChange,
    pageNumber,
    hasFetchedOnce,
    totalRecords,
    currentSort,
    handleSortChange,
  };
};
